import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, Compass } from "lucide-react";
import { fetchProducts, api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ASMRGallery from "@/components/ASMRGallery";
import SovereignProvisions from "@/components/SovereignProvisions";
import CureTracker from "@/components/CureTracker";
import CyberOpsHero from "@/components/CyberOpsHero";
import PartnerInvitationSection from "@/components/PartnerInvitationSection";
import { PRODUCTS as INVENTORY_SOAPS } from "@/pages/Inventory";
import { useTenant } from "@/context/TenantContext";
import { useLang } from "@/context/LanguageContext";

// Director K (2026-10-30): show all 7 until Ymaku confirms which are real.
const INVENTORY_CARDS = INVENTORY_SOAPS.map((s) => ({
    slug: s.slug,
    name: s.name,
    tagline: s.tagline,
    image: s.image,
    price: null,
    collection: "Coming soon",
    category: "soap",
    source_type: "island_gold",
    to: "/inventory",
}));

function HomeHero() {
    const { t } = useLang();
    return (
        <section className="relative bg-cream overflow-hidden" data-testid="home-hero">
            <div
                className="absolute inset-0 opacity-[0.22]"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=2000&q=80')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "grayscale(0.4) sepia(0.1) saturate(0.8)",
                }}
                aria-hidden
            />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(245,232,200,0.05) 0%, rgba(245,232,200,0.85) 60%, var(--ig-cream) 100%)",
                }}
                aria-hidden
            />
            <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 pb-16 md:pt-24 md:pb-20 min-h-[60vh] flex flex-col justify-center">
                <div className="max-w-3xl rise-in">
                    <span className="divider-rule text-brown" data-testid="home-hero-badge">
                        <Compass size={12} /> {t("hero.badge")}
                    </span>
                    <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.98] text-gold mt-6 text-glow-gold">
                        {t("hero.h1.l1")}
                        <br />
                        <span className="italic">{t("hero.h1.l2")}</span>
                        <br />
                        <span className="relative inline-block">
                            {t("hero.h1.l3")}
                            <Sparkles size={28} className="inline-block ml-3 floaty text-gold" aria-hidden />
                        </span>
                    </h1>
                    <p className="mt-8 max-w-xl text-base md:text-lg text-cream/80 leading-relaxed" data-testid="home-hero-line">
                        {t("hero.subhead")}
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a href="#made-by-island-gold" className="btn-pill btn-primary" data-testid="hero-shop-btn">
                            {t("hero.cta.shop")} <ArrowRight size={16} />
                        </a>
                        <Link to="/story" className="btn-pill btn-ghost" data-testid="hero-story-btn">
                            {t("hero.cta.story")}
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function MadeByIslandGold({ products, titles }) {
    const cards = [...products, ...INVENTORY_CARDS];
    return (
        <section id="made-by-island-gold" className="relative bg-cream py-16 md:py-24" data-testid="home-collection">
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <span className="divider-rule text-brown" data-testid="home-made-kicker">{titles.made_kicker}</span>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-forest mt-5 leading-[1.02]">
                            {titles.made_title}
                        </h2>
                    </div>
                    <Link
                        to="/inventory#view-all"
                        className="text-[0.72rem] tracking-[0.3em] uppercase text-forest hover:text-gold transition-colors inline-flex items-center gap-2"
                        data-testid="home-view-all-link"
                    >
                        View all <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                    {cards.map((p, i) => (
                        <ProductCard key={p.slug} product={p} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function GiveBack({ isCuring }) {
    return (
        <section className="bg-forest text-cream py-16 md:py-20 relative overflow-hidden" data-testid="home-philanthropy">
            <div className="grain absolute inset-0" />
            <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
                <span className="divider-rule text-gold">Give Back · 10%</span>
                <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.05]">
                    Every bar funds
                    <br />
                    <span className="italic text-gold">CyberKids Academy.</span>
                </h2>
                <p className="mt-6 text-cream/75 max-w-xl mx-auto leading-relaxed">
                    Toggle the 10% contribution at checkout — it goes straight to
                    code-literacy scholarships for Hawaiian youth.
                </p>
                <Link to={isCuring ? "/coming-soon" : "/inventory"} className="btn-pill btn-gold mt-10" data-testid="philanthropy-shop-btn">
                    Shop with Purpose
                </Link>
            </div>
        </section>
    );
}

export default function Home() {
    const [products, setProducts] = useState([]);
    const [fleetMode, setFleetMode] = useState("curing");
    const [titles, setTitles] = useState({
        made_kicker: "Made by Island Gold",
        made_title: "Our soaps & skincare.",
        picks_kicker: "Island Botanicals",
        picks_title: "Bath companions we love.",
    });
    const { tenant } = useTenant();
    const isCyberOps = tenant?.sector === "cyberops";

    useEffect(() => {
        if (isCyberOps) return;
        api.get("/store/settings")
            .then(({ data }) => setFleetMode(data.fleet_mode || "curing"))
            .catch(() => setFleetMode("curing"));
        fetchProducts().then(setProducts).catch(() => setProducts([]));
        api.get("/public/home-sections").then(({ data }) => setTitles(data)).catch(() => {});
    }, [isCyberOps]);

    if (isCyberOps) {
        return (
            <div data-testid="home-page" data-tenant-sector="cyberops">
                <CyberOpsHero />
            </div>
        );
    }

    const isCuring = fleetMode === "curing";
    return (
        <div data-testid="home-page" data-fleet-mode={fleetMode}>
            <HomeHero />
            <MadeByIslandGold products={products} titles={titles} />
            {/* Affiliate picks · renders only when a real, disclosed link is attached */}
            <SovereignProvisions variant="secondary" kicker={titles.picks_kicker} title={titles.picks_title} />
            {isCuring && <CureTracker />}
            <ASMRGallery />
            <GiveBack isCuring={isCuring} />
            {/* Partner invitation · bottom of homepage · owner-managed wording */}
            <PartnerInvitationSection />
        </div>
    );
}
